import { useCallback, useState, type ClipboardEvent, type DragEvent } from 'react';
import { useAppSettings } from '../settings/api';

/**
 * Letters here are written, not pasted. A short quote is fine; a pasted essay
 * (or one line pasted until the counter turns green) is turned back with a
 * gentle note. The admin dial `maxPasteChars` sets the size; 0 allows any paste.
 *
 * Only the composer is guarded — the server cannot tell a paste from typing, so
 * this is a nudge, and the quality floor is what actually holds the line.
 */
export function usePasteGuard() {
  const { maxPasteChars } = useAppSettings();
  const [notice, setNotice] = useState<string | null>(null);

  const refuse = useCallback(
    (text: string, prevent: () => void) => {
      if (maxPasteChars <= 0 || text.length <= maxPasteChars) {
        setNotice(null);
        return;
      }
      prevent();
      setNotice(
        `That’s a long paste. Letters here are written by hand, so pastes are kept to ${maxPasteChars} characters. Quote a line if you like, and write the rest yourself.`,
      );
    },
    [maxPasteChars],
  );

  const onPaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => refuse(e.clipboardData.getData('text'), () => e.preventDefault()),
    [refuse],
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLTextAreaElement>) => refuse(e.dataTransfer.getData('text'), () => e.preventDefault()),
    [refuse],
  );

  const clearNotice = useCallback(() => setNotice(null), []);

  return { onPaste, onDrop, notice, clearNotice };
}
